/**
 * Auth Service
 *
 * Handles all authentication API calls including login, 2FA, phone registration,
 * token refresh, logout, and impersonation.
 */

import axios, { AxiosError } from "axios";
import { getApiUrl } from "@/shared/utils/api";
import { clearPersistedFilters } from "@/shared/utils/persistedFilters";
import { AUTH_CONSTANTS } from "../configs/constants";
import { AUTH_ENDPOINTS } from "../configs/endpoints";
import {
  type AuthResponse,
  type AuthWith2FactorResponse,
  type AuthLoginResponse,
  isAuthLoginResponse,
  isException,
  isMissingCellPhoneNumber,
} from "../types/auth.types";
import {
  deleteAuthInfo, getAccessToken, getAuthTokenRemainingTime, publishAuthToken, saveAuthToken,
} from "./accessToken.service";
import { getAuthDeviceId } from "./localStorage.service";

// =============================================================================
// TYPES
// =============================================================================

export type LoginPayload = {
  email: string
  password: string
  captchaToken: string
};

export type Login2FAPayload = {
  email: string
  password: string
  otp_challenge: string
  otp_code: string
};

export type RegisterPhonePayload = {
  email: string
  password: string
  cell_phone: string
};

export type ImpersonatePayload = {
  organizationId: string
  token: string
};

export type RefreshTokenResponse = { access_token: string };

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create OAuth payload with common fields
 */
const createOAuthPayload = (email: string, password: string) => ({
  grant_type: AUTH_CONSTANTS.GRANT_TYPE.PASSWORD,
  scope: AUTH_CONSTANTS.SCOPE.REPRESENTATIVE,
  username: email,
  password,
  device_id: getAuthDeviceId(),
});

/**
 * Create unknown error response
 */
const createUnknownError = (description: string) => ({
  response: {
    data: {
      error: {
        code: AUTH_CONSTANTS.ERROR_CODES.UNKNOWN_ERROR,
        title: "Erreur",
        description,
      },
    },
  },
});

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Initial login with email/password/captcha
 * Returns success, 2FA required, missing phone, or error
 */
export const login = async ({
  email,
  password,
  captchaToken,
}: LoginPayload): Promise<AuthResponse> => {
  try {
    const response = await axios.post<AuthResponse>(
      getApiUrl(AUTH_ENDPOINTS.LOGIN_2FA),
      {
        ...createOAuthPayload(email, password),
        hcaptcha: captchaToken,
      },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
        validateStatus: () => true,
      },
    );

    const authData = response.data;

    if (isException(authData) && !isMissingCellPhoneNumber(authData)) {
      throw authData;
    }

    if (isAuthLoginResponse(authData)) {
      saveAuthToken(authData.access_token, authData.expires_in);
    }

    return authData;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw createUnknownError("Une erreur est survenue lors de la connexion");
  }
};

/**
 * Verify 2FA code
 */
export const login2FA = async ({
  email,
  password,
  otp_challenge,
  otp_code,
}: Login2FAPayload): Promise<AuthResponse> => {
  try {
    const response = await axios.post<AuthLoginResponse>(
      getApiUrl(AUTH_ENDPOINTS.VERIFY_2FA),
      {
        ...createOAuthPayload(email, password),
        otp_challenge,
        otp_code,
      },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );

    if (response.status !== 200 && "error" in response.data) {
      throw { response: { data: { error: response.data.error } } };
    }

    const authData = response.data;
    saveAuthToken(authData.access_token, authData.expires_in);
    return authData;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw createUnknownError("Une erreur est survenue lors de la vérification 2FA");
  }
};

/**
 * Register phone number for 2FA
 */
export const registerPhone = async ({
  email,
  password,
  cell_phone,
}: RegisterPhonePayload): Promise<AuthWith2FactorResponse> => {
  const response = await axios.post<AuthWith2FactorResponse>(
    getApiUrl(AUTH_ENDPOINTS.REGISTER_PHONE),
    {
      ...createOAuthPayload(email, password),
      cell_phone: cell_phone,
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    },
  );
  return response.data;
};

/**
 * Admin impersonation
 */
export const impersonate = async ({
  organizationId,
  token,
}: ImpersonatePayload): Promise<AuthLoginResponse> => {
  const response = await axios.post<AuthLoginResponse>(
    getApiUrl(AUTH_ENDPOINTS.IMPERSONATE),
    {
      organization_id: organizationId,
      device_id: getAuthDeviceId(),
    },
    {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      withCredentials: true,
    },
  );

  if (response.status !== 200) throw new Error("Login failed");

  // Impersonation switches to another CSE: the previous session's filters do not apply.
  clearPersistedFilters();
  saveAuthToken(response.data.access_token, response.data.expires_in);
  return response.data;
};

/**
 * Exchange grant login token for access token (impersonation via code)
 */
export const exchangeGrantLogin = async (loginToken: string): Promise<AuthLoginResponse> => {
  deleteAuthInfo();
  clearPersistedFilters();

  const response = await axios.post<AuthLoginResponse>(
    getApiUrl(AUTH_ENDPOINTS.EXCHANGE_GRANT_LOGIN),
    { login_token: loginToken, device_id: getAuthDeviceId() },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    },
  );

  if (response.status !== 200) throw new Error("Grant exchange failed");

  saveAuthToken(response.data.access_token, response.data.expires_in);
  return response.data;
};

// Web Lock name shared by all tabs of the app on the same origin. Kept from the version that
// stored the token: during a deployment, an old tab and a new one still take turns.
const TOKEN_REFRESH_LOCK_NAME = "yeati_mini_token_refresh";

// Don't rotate the token if it still has at least this much lifetime left.
const TOKEN_FRESHNESS_MARGIN_MS = 2 * 60 * 1000;

// Refresh of this tab in flight: concurrent callers share it instead of queuing a second one.
let pendingRefresh: Promise<string> | null = null;

/**
 * The token in memory, unless it is the one the API just rejected or is about to expire.
 * Another tab may have put it there: tabs publish the token they obtained.
 */
const usableToken = (rejectedToken?: string): string | null => {
  const accessToken = getAccessToken();
  if (!accessToken || accessToken === rejectedToken) return null;
  return getAuthTokenRemainingTime() > TOKEN_FRESHNESS_MARGIN_MS ? accessToken : null;
};

/**
 * POST /oauth/refresh_token. The refresh token is the httpOnly cookie the browser
 * sends: no bearer, the token in memory may be absent or older than the cookie.
 */
const requestNewToken = async (): Promise<string> => {
  const response = await axios.post(
    getApiUrl(AUTH_ENDPOINTS.REFRESH_TOKEN),
    {
      grant_type: AUTH_CONSTANTS.GRANT_TYPE.REFRESH_TOKEN,
      scope: AUTH_CONSTANTS.SCOPE.REPRESENTATIVE,
      device_id: getAuthDeviceId(),
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    },
  );

  if (response.status !== 200) throw new Error("Refresh token failed");

  saveAuthToken(response.data.access_token, response.data.expires_in);
  publishAuthToken();

  return response.data.access_token;
};

/**
 * Get a usable access token, refreshing it when needed
 *
 * The refresh token (httpOnly cookie shared by all tabs) rotates on every use:
 * concurrent refreshes from several tabs race on it, the losers getting a 401
 * (and soon the revocation of the whole session). So refreshes are serialized
 * across tabs with the Web Locks API, and a tab that gets the lock first reuses
 * the token another tab published while it was waiting. Should that message come
 * after the lock, the tab refreshes with the cookie the other tab already rotated:
 * one rotation more, never two tabs on the same refresh token.
 *
 * @param rejectedToken token the API just answered 401 to: never returned again
 */
export const refreshToken = async (rejectedToken?: string): Promise<string> => {
  const current = usableToken(rejectedToken);
  if (current) return current;

  const refreshUnlessDoneByAnotherTab = async (): Promise<string> =>
    usableToken(rejectedToken) ?? requestNewToken();

  pendingRefresh ??= (
    typeof navigator !== "undefined" && navigator.locks
      ? navigator.locks.request(TOKEN_REFRESH_LOCK_NAME, refreshUnlessDoneByAnotherTab)
      // Without Web Locks, tabs may race on the cookie; this tab at least refreshes once at a time.
      : refreshUnlessDoneByAnotherTab()
  ).finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
};

/**
 * Revoke token (logout)
 */
export const revokeToken = (): void => {
  const accessToken = getAccessToken();
  if (!accessToken) return;

  const deviceId = getAuthDeviceId();
  axios.post(
    getApiUrl(AUTH_ENDPOINTS.LOGOUT),
    {
      device_id: deviceId,
      scope: AUTH_CONSTANTS.SCOPE.REPRESENTATIVE,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      withCredentials: true,
    },
  ).catch(() => {
    // Revocation is best effort: local logout must succeed even if the API rejects it.
  });
};

// =============================================================================
// PUBLIC CONFIG
// =============================================================================

export type NewVersionDetails = {
  title: string
  content: string
  version: string
};

export type PublicConfig = {
  version: string
  captcha: boolean
  psp_stripe: boolean
  psp_paybox: boolean
  new_version: boolean
  new_version_details: NewVersionDetails
};

/**
 * Fetch public config (version, captcha, etc.)
 */
export const getPublicConfig = async (
  version: string,
  device: string,
): Promise<PublicConfig> => {
  const response = await axios.get<PublicConfig>(
    getApiUrl(`${AUTH_ENDPOINTS.CONFIG}?version=${version}&device=${device}`),
  );
  return response.data;
};
